import type { OperatorAuditEntry } from '@forecastify/shared';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ An audit row's *Változás* column: `Team → Business`, `reason: …`.         │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **Values, never labels.** The before/after diff carries ids, slugs and free text in whatever
 * shape the action wrote; this prints the fields that changed, side by side, and translates nothing
 * — a reason is the operator's own words and an id is an id. The mockup's column is the same: data.
 *
 * ⚠️ **A key present on only one side is printed alone**, so a creation reads as its values and a
 * removal as what was removed, rather than as `undefined → …`.
 */
export function describeChange(entry: OperatorAuditEntry): string {
  const before = asRecord(entry.before);
  const after = asRecord(entry.after);
  const keys = [...new Set([...Object.keys(after), ...Object.keys(before)])];

  return keys
    .map((key) => {
      const was = show(before[key]);
      const now = show(after[key]);
      if (was === '' && now === '') return '';
      if (was === '') return now;
      if (now === '') return was;
      if (was === now) return now;
      return `${was} → ${now}`;
    })
    .filter((part) => part !== '')
    .join(' · ');
}

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function show(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}
